import React, { useMemo } from "react";
import { Box, Divider, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { FolderClosed, FolderTree, GraduationCap } from "lucide-react";
import SelectCst from "@components/InputCst/SelectCst";
import DataPickerCst from "@components/InputCst/DataPickerCst";
import InputCst from "@components/InputCst/InputCst";
import MultiSelectCst from "@components/InputCst/MultiSelectCst";
import InfoBannerCst from "@components/InfoBannerCst";
import { WizardStepProps } from "@components/WizardCst/types";
import { useAttributesList } from "@pages/Contents/V2/Corporate/Catalog/Enrollment/hooks/useAttributesList";
import {
  AttributeOption,
  EnrollType,
  ExpirationType,
} from "@pages/Contents/V2/Corporate/Catalog/Enrollment/types";
import {
  ENROLL_TYPE_OPTIONS,
  EXPIRATION_TYPE_OPTIONS,
  buildEnrollTypePatch,
  isAutonomousEnrollType,
  isTopEnrollType,
  resolveExpirationKind,
  validateConfig,
} from "@pages/Contents/V2/Corporate/Catalog/Enrollment/config/enrollmentFieldsConfig";
import { EnrollmentStepData } from "../types";
import { StepContainer } from "../../Wizard/components/StepLayout";
import { Toggle } from "src/UI/Toggle";
import { PERMISSIONS, usePermissions } from "@utils/permissions";

const EnrollmentModeStep: React.FC<
  WizardStepProps<EnrollmentStepData> & { hiddenEnrollTypes?: string[] }
> = ({ data, setData, hiddenEnrollTypes }) => {
  const { t } = useTranslation("group-wizard-v2");
  const { t: tEnroll } = useTranslation("enrollment-modal");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();

  // Come P74 (wizard Iscrivi del catalogo) e v1: attributi di iscrizione
  // visibili e caricati solo con fe.enrollment.attributes.read.
  const { can } = usePermissions();
  const canViewAttributes = can(PERMISSIONS.enrollment.attributes);
  const { options: attributes } = useAttributesList(
    canViewAttributes ? corporateId : undefined,
  );

  const config = data.enrollConfig;
  const errors = validateConfig(config).errors;

  const update = (patch: Partial<typeof config>) =>
    setData((prev) => ({ enrollConfig: { ...prev.enrollConfig, ...patch } }));

  const counts = useMemo(() => {
    const acc = { activity: 0, course: 0, path: 0 };
    data.loSelections.forEach((s) => {
      if (s.type === "course") acc.course += 1;
      else if (s.type === "path") acc.path += 1;
      else acc.activity += 1;
    });
    return acc;
  }, [data.loSelections]);

  const recapSegments = [
    {
      count: counts.activity,
      label: t("enrollMode.activities"),
      Icon: GraduationCap,
    },
    {
      count: counts.course,
      label: t("enrollMode.courses"),
      Icon: FolderClosed,
    },
    { count: counts.path, label: t("enrollMode.paths"), Icon: FolderTree },
  ].filter((s) => s.count > 0);

  const enrollTypeOptions = ENROLL_TYPE_OPTIONS.filter(
    (o) => !hiddenEnrollTypes?.includes(o.value),
  ).map((o) => ({
    value: o.value,
    label: String(tEnroll(o.labelKey, o.value)),
  }));

  const expirationKind = resolveExpirationKind(config);
  const isAutonomous = isAutonomousEnrollType(config.enrollType);
  const isForcedExpirationDisabled =
    !isTopEnrollType(config.enrollType) || expirationKind !== "fixed-date";

  const renderInlineSwitch = (
    label: string,
    checked: boolean,
    onChange: (next: boolean) => void,
    disabled = false,
  ) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "4px 0",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 700,
          color: theme.palette.text.primary,
        }}
      >
        {label}
      </Typography>
      <Toggle checked={checked} disabled={disabled} onChange={onChange} />
    </Box>
  );

  return (
    <StepContainer>
      {recapSegments.length > 0 && (
        <InfoBannerCst>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{ fontSize: "14px", fontWeight: 700, color: "inherit" }}
            >
              {t("enrollMode.recapPrefix")}
            </Typography>
            {recapSegments.map(({ count, label, Icon }, index) => (
              <React.Fragment key={label}>
                {index > 0 && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ my: 0.5, borderColor: "currentColor", opacity: 0.3 }}
                  />
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Icon size={18} />
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 600, color: "inherit" }}
                  >
                    {count} {label}
                  </Typography>
                </Box>
              </React.Fragment>
            ))}
          </Box>
        </InfoBannerCst>
      )}

      <Box
        sx={{
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
        }}
      >
        <SelectCst
          id="group-enrollment-enrollType"
          label={tEnroll("config.enroll-type.label")}
          value={config.enrollType ?? ""}
          options={enrollTypeOptions}
          optionsToDisable={["AUTO", "BOTTOM"]}
          onChange={(v: any) =>
            update(buildEnrollTypePatch(config, v as EnrollType))
          }
          required
          error={!!errors.enrollType}
          errorMessage={errors.enrollType ? t("validation.required") : ""}
        />
      </Box>

      <Divider />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {tEnroll("config.enable-expiration.label")}
          </Typography>
          <Typography
            sx={{ fontSize: "12px", color: theme.palette.text.tertiary }}
          >
            {tEnroll("config.enable-expiration.helper")}
          </Typography>
        </Box>
        <Toggle
          noLabel={tEnroll("common.no")}
          yesLabel={tEnroll("common.yes")}
          checked={config.enableExpiration}
          onChange={(next) =>
            update(
              next
                ? { enableExpiration: true, expirationKind: "fixed-date" }
                : {
                    enableExpiration: false,
                    expirationKind: undefined,
                    expirationType: undefined,
                    expirationDate: undefined,
                    daysToComplete: undefined,
                    forcedExpiration: false,
                  },
            )
          }
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {renderInlineSwitch(
          tEnroll("config.expiration-kind.options.completion-days-switch"),
          config.enableExpiration && expirationKind === "completion-days",
          (next) =>
            update({
              expirationKind: next ? "completion-days" : "fixed-date",
              ...(next
                ? { expirationDate: undefined, forcedExpiration: false }
                : { daysToComplete: undefined }),
            }),
          !config.enableExpiration || isAutonomous,
        )}

        {renderInlineSwitch(
          tEnroll("config.forced-expiration.label"),
          !!config.forcedExpiration && !isForcedExpirationDisabled,
          (next) => update({ forcedExpiration: next }),
          isForcedExpirationDisabled,
        )}

        <SelectCst
          id="group-enrollment-expirationType"
          label={tEnroll("config.expiration-type.label")}
          value={config.expirationType ?? ""}
          options={EXPIRATION_TYPE_OPTIONS.map((o) => ({
            value: o.value,
            label: String(tEnroll(o.labelKey, o.value)),
          }))}
          onChange={(v: any) => update({ expirationType: v as ExpirationType })}
          disabled={!config.enableExpiration}
          required={config.enableExpiration}
          error={!!errors.expirationType}
          errorMessage={errors.expirationType ? t("validation.required") : ""}
        />

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <DataPickerCst
            id="group-enrollment-expirationDate"
            label={tEnroll("config.expiration-date.label")}
            value={config.expirationDate ?? null}
            onChange={(v: any) =>
              update({
                expirationDate: v
                  ? typeof v === "string"
                    ? v
                    : v?.toISOString?.()
                  : undefined,
              })
            }
            disabled={
              !config.enableExpiration || expirationKind !== "fixed-date"
            }
            required={
              config.enableExpiration && expirationKind === "fixed-date"
            }
            error={!!errors.expirationDate}
            errorMessage={errors.expirationDate ? t("validation.required") : ""}
          />

          <InputCst
            id="group-enrollment-daysToComplete"
            label={tEnroll("config.days-to-complete.label")}
            type="number"
            value={
              config.daysToComplete === undefined
                ? ""
                : String(config.daysToComplete)
            }
            onChange={(e: any) => {
              const raw = e?.target?.value;
              update({
                daysToComplete: raw === "" ? undefined : Number(raw),
              });
            }}
            disabled={
              !config.enableExpiration || expirationKind !== "completion-days"
            }
            required={
              config.enableExpiration && expirationKind === "completion-days"
            }
            error={!!errors.daysToComplete}
            errorMessage={errors.daysToComplete ? t("validation.required") : ""}
          />
        </Box>
      </Box>

      {canViewAttributes && <Divider />}

      {canViewAttributes && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
            {tEnroll("config.attributes.label")}
          </Typography>
          <Typography
            sx={{ fontSize: "12px", color: theme.palette.text.tertiary, mb: 1 }}
          >
            {tEnroll("config.attributes.helper")}
          </Typography>
          <Box sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
            <MultiSelectCst
              id="group-enrollment-attributes"
              label={tEnroll("config.attributes.placeholder")}
              value={attributes.filter((a) =>
                config.attributesIds.includes(a.value),
              )}
              onChange={(next: any) => {
                const items = Array.isArray(next) ? next : [];
                update({
                  attributesIds: items
                    .map((it: AttributeOption | number) =>
                      typeof it === "number" ? it : it?.value,
                    )
                    .filter((v): v is number => typeof v === "number"),
                });
              }}
              options={attributes}
            />
          </Box>
        </Box>
      )}
    </StepContainer>
  );
};

export default EnrollmentModeStep;
